import React from 'react'
import { useForm } from 'react-hook-form'
import {
  Mail,
  Phone,
  Linkedin,
  MessageCircle,
  BarChart2,
  FileText,
  ArrowRightLeft,
  Presentation,
  DollarSign,
  Facebook
} from 'lucide-react'
import { InstagramLogoIcon } from '@radix-ui/react-icons'

import Fundo from './Images/Klinger/Fundo.png'
import Login from './Images/Klinger/Login.png'
import Logo from './Images/Klinger/Logo.png'
import type { UseFormRegisterReturn } from 'react-hook-form'
/**
 * Types
 */
interface FormData {
  name: string
  tel: string
  email: string
  message: string
}

/**
 * Main page – mobile‑first & fully responsive
 */
const KlingerPage: React.FC = () => (
  <div className="min-h-screen flex flex-col bg-background font-sans text-xs sm:text-sm md:text-base">
    <Header />
    <Hero />

    {/* CONTENT */}
    <main className="flex flex-col items-center bg-neutral-200/90 px-4 pb-16">
      <InfoSection />
      <AreasOfExpertise />
      <ContactInfo />
      <ContactForm />
    </main>

    <Footer />
  </div>
)

/**
 * Header – sticks to top, responsive paddings
 */
const Header: React.FC = () => (
  <header className="relative z-20 flex items-center justify-between bg-[#151A29] text-white px-4 py-3 sm:px-6">
    <img src={Logo} alt="Klinger Consults" className="w-8 sm:w-10" />

    <nav className="flex items-center gap-2 text-[10px] sm:text-xs md:text-sm">
      {[
        { label: 'Seja um Parceiro', href: '#' },
        { label: 'Serviços', href: '#' },
        { label: 'Contato', href: '#' }
      ].map(item => (
        <a
          key={item.label}
          href={item.href}
          className="rounded-md border border-white/20 bg-[#22293E] px-2 py-1 hover:bg-[#1F242F] transition-colors"
        >
          {item.label}
        </a>
      ))}
      <img src={Login} alt="Menu" className="w-4 sm:w-5" />
    </nav>
  </header>
)

/**
 * Hero – uses background‑image for full‑width cover
 */
const Hero: React.FC = () => (
  <section
    className="relative flex flex-col justify-center gap-6 bg-cover bg-center text-white px-6 py-16 sm:py-20 lg:h-[70vh]"
    style={{ backgroundImage: `url(${Fundo})` }}
  >
    <h1 className="max-w-md text-xl font-bold leading-tight sm:text-2xl">
      Impostos a mais viram soluções para sua empresa!
    </h1>
    <p className="max-w-md text-[11px] sm:text-sm font-medium">
      Pare de perder dinheiro com impostos. Transforme em recursos para sua empresa!
    </p>
    <a
      href="#contato"
      className="inline-block  rounded-xl bg-[#1F74A8] px-6 py-2 text-xs font-medium transition-colors hover:bg-[#175C86] sm:text-sm"
    >
      ENTRE EM CONTATO
    </a>
  </section>
)

/**
 * Info section – readable widths & responsive typography
 */
const InfoSection: React.FC = () => (
  <section className="prose prose-sm sm:prose-base lg:prose-lg max-w-none text-black mt-6">
    <p>
      A carga tributária no Brasil está entre as mais altas do mundo!{' '}
      <strong>Será que você está pagando mais do que deveria?</strong>
    </p>

    <h2>Por que isso acontece?</h2>
    <ul className="list-disc pl-4">
      <li>
        <strong>Complexidade tributária:</strong> O sistema de impostos no Brasil é complicado e frequentemente
        alterado, tornando fácil cometer erros.
      </li>
      <li className="mt-2">
        <strong>Falta de conhecimento especializado:</strong> Nem sempre as empresas têm acesso a especialistas que
        conheçam profundamente a legislação tributária e identifiquem oportunidades de recuperação.
      </li>
    </ul>

    <h2>A solução: Recuperação de Crédito Tributário</h2>
    <p>
      Se sua empresa pagou impostos indevidos ou a maior, você pode recuperar esses valores! Trata‑se de um direito
      garantido por lei e uma forma de melhorar o fluxo de caixa da sua empresa, tornando‑a mais competitiva no mercado.
    </p>

    <h2>Benefícios para sua empresa</h2>
    <ul className="list-disc pl-4">
      <li>Recuperação de valores que podem ser reinvestidos no crescimento do negócio.</li>
      <li>Correção de possíveis erros na apuração, evitando problemas futuros com o fisco.</li>
      <li>Planejamento tributário eficiente, reduzindo custos desnecessários.</li>
    </ul>

    <h2>Por que escolher a nossa empresa?</h2>
    <p>
      Somos especialistas em identificar e recuperar créditos tributários, com equipe qualificada e experiência
      comprovada. Nosso trabalho é ajudar você a reduzir custos e aproveitar todas as oportunidades de economia
      permitidas pela legislação.
    </p>

    <p className="font-bold">
      Não deixe seu dinheiro nas mãos de terceiros! Entre em contato hoje mesmo e descubra quanto sua empresa pode
      recuperar.
    </p>
  </section>
)

/**
 * Areas of expertise – cards wrap on larger screens
 */
const AreasOfExpertise: React.FC = () => (
  <section className="mx-auto mt-10 w-full max-w-lg rounded-[38px] bg-[#4A5763] p-6 text-white">
    <h2 className="text-center text-xl font-bold">Áreas de Atuação</h2>

    <div className="mt-6 grid grid-cols-2 gap-4 text-[10px] font-semibold sm:text-xs lg:text-sm">
      <ExpertiseCard icon={BarChart2} label="Assessoria Tributária" />
      <ExpertiseCard icon={FileText} label="Jurídico Tributário" />
      <ExpertiseCard icon={ArrowRightLeft} label="Licitações" />
      <ExpertiseCard icon={Presentation} label="Consultoria" />
      <ExpertiseCard icon={DollarSign} label="Contabilidade" />
      <ExpertiseCard icon={MessageCircle} label="Atendimento Personalizado" />
    </div>
  </section>
)

interface ExpertiseCardProps {
  icon: React.ElementType
  label: string
}
const ExpertiseCard: React.FC<ExpertiseCardProps> = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-2 rounded-xl bg-white/20 p-3 text-center">
    <Icon className="w-5 h-5" />
    {label}
  </div>
)

/**
 * Contact fixed information
 */
const ContactInfo: React.FC = () => (
  <section id="contato" className="mt-12 flex flex-col items-center text-center">
    <h2 className="text-xl font-bold text-[#424242]">Contato</h2>
    <p className="mt-4 max-w-md text-black">
      De segunda à sexta-feira das 09h00 às 18h00 <br /> ou pelo formulário abaixo.
    </p>

    <div className="mt-6 flex max-w-md flex-col gap-4 rounded-2xl bg-white/40 p-6 text-sm text-black">
      <a href="mailto:contato@kligerconsults.com.br" className="flex items-center gap-2 hover:underline">
        <Mail className="w-4" /> contato@kligerconsults.com.br
      </a>
      <a href="tel:+553100000000" className="flex items-center gap-2 hover:underline">
        <Phone className="w-4" /> (31) 0000‑0000
      </a>
    </div>
  </section>
)

/**
 * Contact form – react‑hook‑form
 */
const ContactForm: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    console.log('Form data:', data)
    await new Promise(r => setTimeout(r, 800))
    reset()
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="mx-auto mt-10 w-full max-w-md space-y-4 rounded-[31px] bg-neutral-100 p-8"
    >
      {/* Row 1 */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <InputField id="name" label="Nome*" error={!!errors.name} register={register('name', { required: true })} />
        <InputField id="tel" label="Telefone*" error={!!errors.tel} register={register('tel', { required: true })} />
      </div>

      <InputField
        id="email"
        label="Email*"
        type="email"
        error={!!errors.email}
        register={register('email', {
          required: true,
          pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
        })}
      />

      <div className="flex flex-col">
        <label htmlFor="message" className="mb-1">
          Mensagem*
        </label>
        <textarea
          id="message"
          className="h-24 resize-none rounded-2xl bg-white/40 p-2 text-sm"
          aria-invalid={errors.message ? 'true' : 'false'}
          {...register('message', { required: true })}
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-xl bg-[#1F74A8] py-2 font-medium text-white transition-colors hover:bg-[#175C86] disabled:opacity-50"
      >
        {isSubmitting ? 'Enviando…' : 'Enviar'}
      </button>
    </form>
  )
}

interface InputFieldProps {
  id: string
  label: string
  type?: string
  error: boolean
  register: UseFormRegisterReturn
}

const InputField: React.FC<InputFieldProps> = ({ id, label, type = 'text', error, register }) => (
  <div className="flex flex-col">
    <label htmlFor={id} className="mb-1">
      {label}
    </label>
    <input
      id={id}
      type={type}
      className={`rounded-2xl bg-white/40 p-2 text-sm ${error ? 'ring-1 ring-red-500' : ''}`}
      aria-invalid={error}
      {...register}
    />
  </div>
)

/**
 * Footer – basic responsive columns
 */
const Footer: React.FC = () => (
  <footer className="mt-auto bg-[#4A5763] p-6 text-white">
    <h3 className="text-center text-sm font-semibold">SIGA‑NOS</h3>

    <div className="mx-auto mt-4 flex max-w-md flex-col items-center gap-6 sm:flex-row sm:justify-between">
      <p className="text-center text-xs sm:text-sm">Acompanhe a Klinger Consults nas redes sociais</p>

      <div className="flex items-center gap-4">
        <SocialIcon Icon={Facebook} href="#" label="Facebook" />
        <SocialIcon Icon={InstagramLogoIcon} href="#" label="Instagram" />
        <SocialIcon Icon={Linkedin} href="#" label="LinkedIn" />
      </div>
    </div>

    <hr className="my-6 border-white/50" />

    <div className="mx-auto grid max-w-md grid-cols-1 gap-6 text-xs sm:grid-cols-2">
      <div className="flex items-start gap-4">
        <img src={Logo} alt="Klinger" className="w-14" />
        <nav className="space-y-1">
          {[
            { label: 'Home', href: '#' },
            { label: 'Contato', href: '#' },
            { label: 'Serviços', href: '#' }
          ].map(item => (
            <a key={item.label} href={item.href} className="block hover:underline">
              {item.label}
            </a>
          ))}
        </nav>
      </div>

      <address className="not-italic">
        <h4 className="font-semibold">Endereço</h4>
        Rua XX
        <br />
        Belo Horizonte ‑ 99999‑999, BR
      </address>
    </div>
  </footer>
)

interface SocialIconProps {
  Icon: React.ElementType
  href: string
  label: string
}
const SocialIcon: React.FC<SocialIconProps> = ({ Icon, href, label }) => (
  <a href={href} aria-label={label} className="transition-transform hover:scale-110">
    <Icon className="h-8 w-8" />
  </a>
)

export default KlingerPage
